import { App, Button, Card, Modal, Segmented, Space, Switch, Tag, Tooltip, message } from "antd";
import {
  CameraOutlined,
  DownloadOutlined,
  EnvironmentOutlined,
  ExclamationCircleFilled,
} from "@ant-design/icons";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import L from "leaflet";
import "leaflet-draw/dist/leaflet.draw.css";
// Side-effect import — leaflet-draw extends the `L` namespace at runtime.
import "leaflet-draw";

import { CustomMap } from "../../../components/maps";
import { apiClient } from "../../../services/api/apiClient";
import {
  auditPlotService,
  controlPointService,
  adjacentCropService,
} from "../../../services/api";

// Leaflet drops `_mapPane`/`_loaded` on `map.remove()`; any subsequent
// call on the instance reads `undefined._leaflet_pos` and throws.
// Guard every deferred (rAF/await/timeout) map operation with this.
const isMapAlive = (m: L.Map | null | undefined): m is L.Map =>
  !!m && !!(m as unknown as { _mapPane?: HTMLElement })._mapPane;

type CreateMode =
  | "none"
  | "plot"
  | "plot-gpx"
  | "control-point"
  | "adjacent-crop";

/**
 * Parse a GPX or KML file into a single closed Polygon GeoJSON.
 *
 * Strategy:
 *   1. Detect flavor via root element (`<gpx>` vs `<kml>`).
 *   2. GPX branch:
 *      a. If `<trkpt>` present → perimeter walked by the auditor →
 *         Douglas-Peucker simplify (~1m) + auto-close.
 *      b. Otherwise fall back to `<wpt>` waypoints as polygon corners.
 *   3. KML branch:
 *      a. First `<Polygon>/<outerBoundaryIs>/<LinearRing>/<coordinates>`
 *         (formato KML canónico para polígonos).
 *      b. Si no hay, fallback a `<LineString>/<coordinates>` como
 *         perímetro (mismo trato que el track GPX).
 *      c. Si tampoco, `<Point>` como vértices sueltos.
 *
 * Returns `null` if the file is malformed or has < 3 points.
 */
const parseGpxOrKmlToPolygon = (xml: string): GeoJSON.Polygon | null => {
  let doc: Document;
  try {
    doc = new DOMParser().parseFromString(xml, "application/xml");
  } catch {
    return null;
  }
  if (doc.querySelector("parsererror")) return null;

  const readPoints = (selector: string): [number, number][] => {
    return Array.from(doc.querySelectorAll(selector))
      .map((el) => {
        const lon = parseFloat(el.getAttribute("lon") ?? "");
        const lat = parseFloat(el.getAttribute("lat") ?? "");
        return Number.isFinite(lon) && Number.isFinite(lat)
          ? ([lon, lat] as [number, number])
          : null;
      })
      .filter((p): p is [number, number] => p !== null);
  };

  /**
   * Parsea el texto de un `<coordinates>` de KML. El formato canónico es
   * "lon,lat[,alt]" separado por whitespace (espacios, tabs o newlines).
   * Devuelve la lista filtrando entradas malformadas.
   */
  const readKmlCoordinates = (el: Element | null): [number, number][] => {
    if (!el) return [];
    const raw = (el.textContent || "").trim();
    if (!raw) return [];
    return raw
      .split(/\s+/)
      .map((tuple) => {
        const parts = tuple.split(",");
        if (parts.length < 2) return null;
        const lon = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        return Number.isFinite(lon) && Number.isFinite(lat)
          ? ([lon, lat] as [number, number])
          : null;
      })
      .filter((p): p is [number, number] => p !== null);
  };

  // Douglas-Peucker on lon/lat — tolerance in degrees. 1e-5 ≈ ~1.1m at
  // mid-latitudes, fine for parcels of typical farm size.
  const simplify = (
    points: [number, number][],
    tolerance: number,
  ): [number, number][] => {
    if (points.length <= 2) return points;
    const sqDist = (a: [number, number], b: [number, number]) => {
      const dx = a[0] - b[0];
      const dy = a[1] - b[1];
      return dx * dx + dy * dy;
    };
    const sqSegDist = (
      p: [number, number],
      a: [number, number],
      b: [number, number],
    ) => {
      let x = a[0];
      let y = a[1];
      let dx = b[0] - x;
      let dy = b[1] - y;
      if (dx !== 0 || dy !== 0) {
        const t = ((p[0] - x) * dx + (p[1] - y) * dy) / (dx * dx + dy * dy);
        if (t > 1) {
          x = b[0];
          y = b[1];
        } else if (t > 0) {
          x += dx * t;
          y += dy * t;
        }
      }
      dx = p[0] - x;
      dy = p[1] - y;
      return dx * dx + dy * dy;
    };
    const tol2 = tolerance * tolerance;
    const recurse = (
      pts: [number, number][],
      first: number,
      last: number,
      keep: boolean[],
    ) => {
      let maxSqDist = tol2;
      let index = -1;
      for (let i = first + 1; i < last; i++) {
        const d = sqSegDist(pts[i], pts[first], pts[last]);
        if (d > maxSqDist) {
          index = i;
          maxSqDist = d;
        }
      }
      if (index !== -1) {
        keep[index] = true;
        if (index - first > 1) recurse(pts, first, index, keep);
        if (last - index > 1) recurse(pts, index, last, keep);
      }
    };
    if (sqDist(points[0], points[points.length - 1]) < tol2) {
      // Closed ring: keep simplification stable by working on the open
      // sequence and re-closing at the end.
    }
    const keep = new Array(points.length).fill(false);
    keep[0] = true;
    keep[points.length - 1] = true;
    recurse(points, 0, points.length - 1, keep);
    return points.filter((_, i) => keep[i]);
  };

  const buildPolygon = (raw: [number, number][]): GeoJSON.Polygon | null => {
    if (raw.length < 3) return null;
    let coords = raw;
    // Track points often have hundreds of samples; simplify aggressively.
    if (raw.length > 30) coords = simplify(raw, 1e-5);
    if (coords.length < 3) return null;
    // Auto-close ring.
    const [fx, fy] = coords[0];
    const [lx, ly] = coords[coords.length - 1];
    if (fx !== lx || fy !== ly) coords = [...coords, [fx, fy]];
    return { type: "Polygon", coordinates: [coords] };
  };

  // Detect flavor por el elemento raíz. `documentElement.localName`
  // ignora el prefijo de namespace (`kml:kml` → `kml`) — usarlo evita
  // falsos negativos con archivos que declaran namespace explícito.
  const rootName = (doc.documentElement?.localName || "").toLowerCase();

  if (rootName === "kml") {
    // 1) Polygon (formato canónico KML para parcelas cerradas).
    const polygonCoords = doc.querySelector(
      "Polygon > outerBoundaryIs > LinearRing > coordinates",
    );
    if (polygonCoords) {
      const pts = readKmlCoordinates(polygonCoords);
      if (pts.length >= 3) return buildPolygon(pts);
    }
    // 2) LineString (perímetro dibujado como polilínea) — mismo trato
    //    que el track GPX: simplificar y auto-cerrar.
    const lineCoords = doc.querySelector("LineString > coordinates");
    if (lineCoords) {
      const pts = readKmlCoordinates(lineCoords);
      if (pts.length >= 3) return buildPolygon(pts);
    }
    // 3) Placemarks Point sueltos → vértices del polígono, mismo
    //    fallback que los waypoints GPX.
    const pointNodes = Array.from(
      doc.querySelectorAll("Placemark > Point > coordinates"),
    );
    if (pointNodes.length >= 3) {
      const pts = pointNodes
        .map((el) => readKmlCoordinates(el)[0])
        .filter((p): p is [number, number] => Array.isArray(p));
      if (pts.length >= 3) return buildPolygon(pts);
    }
    return null;
  }

  // Default: GPX (o cualquier XML con la sintaxis GPX de puntos).
  // Try track points first (preferred).
  const trkpts = readPoints("trkpt");
  if (trkpts.length >= 3) return buildPolygon(trkpts);

  // Fall back to waypoints.
  const wpts = readPoints("wpt");
  if (wpts.length >= 3) return buildPolygon(wpts);

  return null;
};

interface AuditMapCardProps {
  auditId: number;
  /** Used in legend labels. */
  auditReference?: string;
  /** Surfaced in the header of the downloadable plot-map image so
   *  the snapshot reads as a self-contained, shareable document. */
  producerName?: string;
  farmName?: string;
  /** Inner map height. Defaults to 540px; pass "100%" when the card
   *  is embedded in a flex column that needs to fill the available
   *  space (e.g. to match a sibling column's height). */
  mapHeight?: string;
  /** When true, the outer Card has `height: 100%` + flex layout so the
   *  map body can absorb the available vertical space. */
  fillHeight?: boolean;
  /** Bump this from the parent to force `fetchMapData()` to re-run —
   *  used by the audit show page after deleting plots / control
   *  points / adjacent crops / samples so the map drops the feature
   *  the user just removed. Cheap monotonic counter; any change in
   *  value triggers a refetch. */
  refreshKey?: number | string;
  /** Hide / disable every "Crear …" action on the map toolbar
   *  (parcela, parcela GPX, punto de control, variedad colindante).
   *  Used when the parent audit is finalised — the map stays
   *  fully navigable but no new feature can be drawn or uploaded. */
  readOnly?: boolean;
  /** Called once both the Leaflet instance is ready AND `mapData`
   *  has loaded (audit plots / farm plots fetched from the API and
   *  drawn as layers). Used by the bulk plot-maps exporter to know
   *  when it's safe to call `composePlotMapImage()` for the current
   *  audit before moving on to the next. Not fired again on
   *  refreshKey bumps — it's a "first ready" signal. */
  onMapReady?: () => void;
  /** Called when the user pulses "Editar" on the popup of a sample
   *  flag. Sample edits happen in a modal owned by the parent
   *  (`show.tsx`) — the map card can't do the navigation itself
   *  because plots/CPs redirect to their own routes but samples
   *  stay in-place on the audit-show page. */
  onEditSample?: (sampleId: string) => void;
}

interface MapFeature {
  type: "Feature";
  geometry: any;
  properties: {
    id?: number | string;
    name?: string;
    surface?: number;
    plot_type?: string;
    pattern?: string;
    layer_type:
      | "audit_plot"
      | "farm_plot"
      | "route"
      | "adjacent_crop"
      | "control_point"
      | "sample"
      // Parcelas licenciadas (Plot vía contrato de la finca). Se
      // envían siempre por `map_data` para que el auditor pueda
      // contrastar la parcela que está validando con el catastro.
      // El toggle del panel de capas las oculta/muestra.
      | "licensed_plot"
      // Parcelas de variedad protegida en el vecindario de la finca
      // (endpoint /audits/{id}/nearby_plots/). Anonimizadas: solo
      // llevan variedad, estado de auditoría, fecha aproximada y
      // distancia. Ayudan a distinguir vecinos con contrato de
      // parcelas "sueltas" cuando el auditor prepara la ruta.
      | "nearby_protected_plot";
    auditor?: string;
    date?: string;
    distance?: number;
    // audit_plot extras
    plantation_frame_row?: number;
    plantation_frame_column?: number;
    plants?: number;
    kg_plot?: number;
    photo_count?: number;
    // adjacent_crop / control_point extras
    species?: string;
    variety?: string;
    reason?: string;
    is_producer_owner?: boolean;
    status?: string;
    /** True when this audit is the one that closed the entity —
     *  control points are painted with a dashed red + ✕ overlay. */
    closed_here?: boolean;
    detection_date?: string | null;
    total_estimated_surface?: number;
    review_count?: number;
    sample_count?: number;
    // sample extras
    /** "tango" / "no_tango" / "pending" — drives the flag colour. */
    // Vocabulario canónico compartido con el KML descargable
    // (`audit_kml.py`). El backend lo emite en `map_data`.
    result_status?:
      | "tango"
      | "no_tango"
      | "na"
      | "pending"
      | "mixed"
      | "nc"
      | "other_variety";
    notes?: string;
    plot_name?: string;
    waypoint?: string;
    state_name?: string;
    // nearby_protected_plot extras (anonimizado — no lleva
    // producer_name ni farm_name, solo lo que el auditor necesita
    // para no confundir vecinos con parcelas sin contrato).
    plot_real_id?: number;
    variety_code?: string | null;
    variety_name?: string | null;
    is_audited?: boolean;
    last_audited_at?: string | null;
    years_since_last_audit?: number | null;
    distance_m?: number | null;
  };
}

interface MapData {
  type: "FeatureCollection";
  features: MapFeature[];
}

type LayerKey = MapFeature["properties"]["layer_type"];

interface LegendItemMeta {
  key: LayerKey;
  /** i18n label key + fallback. */
  labelKey: string;
  labelFallback: string;
  /** Swatch renderer — small inline visual (polygon swatch, pin,
   *  flag) that mirrors how the layer is drawn on the map. */
  swatch: () => React.ReactNode;
}

/** Static metadata table — keep the order stable so the legend
 *  reads the same on every audit. */
const LEGEND_ITEMS: LegendItemMeta[] = [
  {
    key: "audit_plot",
    labelKey: "audit.tabs.plots",
    labelFallback: "Parcelas",
    swatch: () => (
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          background: "#ff8c00",
          opacity: 0.45,
          border: "2px solid #ff8c00",
        }}
      />
    ),
  },
  {
    key: "farm_plot",
    labelKey: "map.farm_geometry",
    labelFallback: "Geometría inicial",
    swatch: () => (
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          background: "rgba(8, 151, 156, 0.08)",
          border: "2px dashed #08979c",
        }}
      />
    ),
  },
  {
    key: "licensed_plot",
    labelKey: "map.licensed_plot",
    labelFallback: "Parcelas licenciadas",
    swatch: () => (
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          background: "rgba(37, 99, 235, 0.15)",
          border: "1.5px solid #1e40af",
        }}
      />
    ),
  },
  {
    key: "control_point",
    labelKey: "map.control_point",
    labelFallback: "Puntos de control",
    swatch: () => (
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 14,
          background: "#ff4d4f",
          borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
        }}
      />
    ),
  },
  {
    key: "adjacent_crop",
    labelKey: "map.adjacent_crop",
    labelFallback: "Variedades colindantes",
    swatch: () => (
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          background: "#722ed1",
          opacity: 0.65,
          borderRadius: 3,
        }}
      />
    ),
  },
  {
    key: "sample",
    labelKey: "map.sample",
    labelFallback: "Muestras",
    swatch: () => (
      // Mini flag: shows all three result colours stacked so the user
      // remembers the colour code at a glance.
      <span style={{ display: "inline-flex", gap: 2 }}>
        {(["#52c41a", "#bfbfbf", "#ff4d4f"] as const).map((c) => (
          <span
            key={c}
            style={{
              display: "inline-block",
              width: 4,
              height: 14,
              background: c,
              border: "1px solid #1a1a1a",
              borderRadius: 1,
            }}
          />
        ))}
      </span>
    ),
  },
  {
    key: "route",
    labelKey: "route.title",
    labelFallback: "Rutas",
    swatch: () => (
      <span
        style={{
          display: "inline-block",
          width: 18,
          height: 0,
          borderTop: "3px solid #1677ff",
          marginBottom: 6,
        }}
      />
    ),
  },
  {
    key: "nearby_protected_plot",
    labelKey: "map.nearby_protected_plot",
    labelFallback: "Vecinos protegidos",
    swatch: () => (
      // Púrpura oscuro rayado — distinto de todo lo demás (naranja,
      // teal, violeta claro adjacent_crop). Auditor identifica "no
      // es mi parcela, es de otra finca del vecindario" con un
      // vistazo.
      <span
        style={{
          display: "inline-block",
          width: 14,
          height: 14,
          borderRadius: 3,
          background:
            "repeating-linear-gradient(45deg, #531dab, #531dab 3px, transparent 3px, transparent 6px)",
          border: "2px solid #531dab",
        }}
      />
    ),
  },
];

/**
 * Horizontal legend strip rendered below the audit map. Each chip
 * shows a swatch matching how the layer is drawn on the map, the
 * label, and the visible-feature count. Clicking a chip toggles
 * that layer's visibility (greyed-out + strikethrough when off).
 */
const MapLegendBar: React.FC<{
  features: MapFeature[];
  visible: Record<LayerKey, boolean>;
  onToggle: (key: LayerKey) => void;
}> = ({ features, visible, onToggle }) => {
  const { t } = useTranslation();
  // Count how many features each layer has so the chip carries a
  // helpful badge like "Parcelas (4)".
  const counts = features.reduce<Record<LayerKey, number>>(
    (acc, f) => {
      const lt = f.properties.layer_type;
      acc[lt] = (acc[lt] ?? 0) + 1;
      return acc;
    },
    {
      audit_plot: 0,
      farm_plot: 0,
      adjacent_crop: 0,
      control_point: 0,
      sample: 0,
      route: 0,
      licensed_plot: 0,
      nearby_protected_plot: 0,
    },
  );
  // Hide chips for layers with zero features — keeps the bar tidy
  // on small audits without geometries.
  const visibleItems = LEGEND_ITEMS.filter((it) => counts[it.key] > 0);
  if (visibleItems.length === 0) return null;
  return (
    <div
      style={{
        marginTop: 8,
        paddingTop: 8,
        borderTop: "1px solid #f0f0f0",
        display: "flex",
        flexWrap: "wrap",
        gap: 8,
        alignItems: "center",
      }}
    >
      {visibleItems.map((it) => {
        const isOn = visible[it.key] !== false;
        return (
          <span
            key={it.key}
            role="button"
            tabIndex={0}
            onClick={() => onToggle(it.key)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onToggle(it.key);
              }
            }}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 10px",
              border: "1px solid",
              borderColor: isOn ? "#d9d9d9" : "#f0f0f0",
              borderRadius: 14,
              background: isOn ? "#fafafa" : "transparent",
              cursor: "pointer",
              userSelect: "none",
              fontSize: 12,
              color: isOn ? "#262626" : "#bfbfbf",
              textDecoration: isOn ? "none" : "line-through",
              transition: "opacity 0.15s, background 0.15s",
              opacity: isOn ? 1 : 0.6,
            }}
            title={
              isOn
                ? `${t(it.labelKey, it.labelFallback)} — ${t("map.legend_hide", "ocultar")}`
                : `${t(it.labelKey, it.labelFallback)} — ${t("map.legend_show", "mostrar")}`
            }
          >
            <span style={{ display: "inline-flex", alignItems: "center", width: 18 }}>
              {it.swatch()}
            </span>
            <span>{t(it.labelKey, it.labelFallback)}</span>
            <span
              style={{
                background: isOn ? "#fff" : "#fafafa",
                border: "1px solid",
                borderColor: isOn ? "#d9d9d9" : "#e8e8e8",
                color: isOn ? "#595959" : "#bfbfbf",
                padding: "0 6px",
                borderRadius: 10,
                fontSize: 11,
                fontWeight: 600,
                minWidth: 18,
                textAlign: "center",
              }}
            >
              {counts[it.key]}
            </span>
          </span>
        );
      })}
    </div>
  );
};

/**
 * Compact map card embedded in the audit detail. Shows the farm
 * polygons (gray) and the audit polygons (orange) for this single
 * audit. Uses the same `CustomMap` (Leaflet) base as the planning map
 * so the controls/look are consistent.
 */
export interface MapCaptureLabel {
  name: string;
  layerType: string;
  /** Container-pixel coordinates of the feature centroid at the
   *  moment of capture. The PDF translates these into mm relative
   *  to the placed image. */
  x: number;
  y: number;
}

export interface MapCapture {
  dataUrl: string;
  /** CSS-pixel size of the map container at capture time — needed
   *  by the PDF builder to scale `labels.x/y` into the image's
   *  printed area without distortion. */
  containerWidth: number;
  containerHeight: number;
  labels: MapCaptureLabel[];
}

export type MapCaptureMode = "main" | "samples";

/** Output of the plot-map composer (header + framed map + footer).
 *  Same blob is downloaded by the in-card "Descargar mapa" button
 *  and bundled into the data-bundle zip by the audit-detail page. */
export interface ComposedPlotMapImage {
  blob: Blob;
  dataUrl: string;
  fileName: string;
}

export interface AuditMapCardHandle {
  /** Capture the current Leaflet view (satellite tiles + every
   *  visible feature) as a PNG dataURL, plus the pixel-space
   *  centroid of every labelled feature. The PDF builder draws
   *  those centroids as labelled pills on top of the placed image
   *  — rendering them in jsPDF is more reliable than trying to
   *  make html-to-image capture Leaflet markers/tooltips.
   *
   *  Modes:
   *  - `main` (default): shows audit plots + farm plots + control
   *    points + adjacent crops, hides samples. Labels every feature
   *    with its name (variety name for adjacent crops).
   *  - `samples`: shows only audit plots (unlabelled) + sample
   *    flags. Labels each sample flag with its waypoint code.
   */
  captureForReport(mode?: MapCaptureMode): Promise<MapCapture | null>;
  /** Compose the same branded plot-map PNG the toolbar button would
   *  download, but return the blob/dataURL so a caller (e.g. the
   *  data-bundle exporter) can use it alongside other artefacts. */
  composePlotMapImage(): Promise<ComposedPlotMapImage | null>;
}

export const AuditMapCard = forwardRef<AuditMapCardHandle, AuditMapCardProps>(({
  auditId,
  auditReference,
  producerName,
  farmName,
  mapHeight = "540px",
  fillHeight = false,
  refreshKey,
  readOnly = false,
  onMapReady,
  onEditSample,
}, ref) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Ant 5: modal via useApp() para que participe del <App> context
  const { modal } = App.useApp();
  const [mapInstance, setMapInstance] = useState<L.Map | null>(null);
  const [mapData, setMapData] = useState<MapData | null>(null);
  const [loading, setLoading] = useState(true);
  const [createMode, setCreateMode] = useState<CreateMode>("none");
  // Per-layer visibility for the legend below the map. Every layer
  // starts visible; clicking a chip in the legend toggles it off.
  // The features-render effect filters by this map, so the layer
  // physically leaves the map (no z-fighting, no popups under
  // hidden geometries).
  const [layerVisible, setLayerVisible] = useState<
    Record<MapFeature["properties"]["layer_type"], boolean>
  >({
    audit_plot: true,
    farm_plot: true,
    adjacent_crop: true,
    control_point: true,
    sample: true,
    route: true,
    // Capa de referencia (licenciadas del contrato). Cargada por
    // `map_data` para dar contexto de catastro sobre la finca. Se
    // enciende por defecto para que el auditor vea de qué se trata
    // sin tener que ir al panel de capas — sobre todo cuando la
    // AuditPlot en curso es aún muy poca cosa.
    licensed_plot: true,
    // Nearby OFF por defecto: es una consulta extra al backend y
    // añade tinta al mapa. Solo la activa quien realmente la
    // quiere ver.
    nearby_protected_plot: false,
  });

  // Radio para la capa "Vecinos protegidos". Slider en la toolbar,
  // opciones estándar (1/2/5/10 km). Cambio de radio dispara
  // refetch del endpoint /nearby_plots/. El backend clampea a
  // [500, 20000] pero desde UI ofrecemos los cuatro pasos.
  const [nearbyRadiusKm, setNearbyRadiusKm] = useState<1 | 2 | 5 | 10>(2);
  const [nearbyFeatures, setNearbyFeatures] = useState<MapFeature[]>([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const toggleLayer = (lt: MapFeature["properties"]["layer_type"]) =>
    setLayerVisible((prev) => ({ ...prev, [lt]: !prev[lt] }));
  // Layer refs so we can clean them up on re-render / unmount.
  // `FeatureGroup` (not LayerGroup) is what exposes `getBounds()`.
  const featuresLayerRef = useRef<L.FeatureGroup | null>(null);
  // Tracks the currently click-highlighted geometry so we can
  // restore its original style when the user opens another feature
  // (Leaflet auto-closes the previous popup) or closes the popup.
  const highlightedRef = useRef<{
    layer: any;
    baseStyle: L.PathOptions;
  } | null>(null);
  // Tracks the `mapData` payload we've already fit-bounded to. We
  // only re-centre when the backend data itself changes — toggling
  // a legend chip rebuilds the layers but must NOT move the view
  // (otherwise hiding/showing the route would jerk the camera
  // around, even though the route is just a reference layer).
  const fittedForMapDataRef = useRef<MapData | null>(null);

  // Loading flag for the "Descargar mapa de parcelas" button — drives
  // its spinner so the user knows the snapshot is in progress.
  const [exportingPlotMap, setExportingPlotMap] = useState(false);
  // Loading flag para "Descargar KML" (parcelas + ruta + muestras +
  // puntos de control + colindantes en un único KML descargable).
  const [downloadingKml, setDownloadingKml] = useState(false);

  const downloadAuditKml = async () => {
    if (!auditId) return;
    setDownloadingKml(true);
    try {
      // Petición binaria (KML es XML). apiClient añade Authorization
      // automáticamente. Usamos responseType='blob' para no perder
      // bytes en un JSON.parse.
      const res = await apiClient.get<Blob>(
        `/audits/${auditId}/download-kml/`,
        { responseType: 'blob' },
      );
      const filename = `auditoria-${auditReference || auditId}.kml`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error: any) {
      console.error('[audit-kml] download failed', error);
      // Fallback silencioso — el user verá que no bajó el archivo.
    } finally {
      setDownloadingKml(false);
    }
  };

  // Compose a clean PNG snapshot of the map with ONLY the plot
  // polygons (audit_plot when present, otherwise farm_plot as the
  // fallback the user requested) and each plot's name rendered on
  // top in a semi-transparent label, wrapped in a branded header +
  // framed body + footer. Returns the result for callers that want
  // to bundle it (the data-bundle exporter), or null if the map
  // has nothing usable. Used by the toolbar button via the
  // `exportPlotMapPng` wrapper that adds the download step + toasts.
  const composePlotMapImage = async (): Promise<ComposedPlotMapImage | null> => {
    if (!mapInstance) return null;
    const container = mapInstance.getContainer() as HTMLElement | null;
    if (!container) return null;

    // Pick the layer family — prefer the audited plots, fall back to
    // the farm-level plot boundary so the user still gets a useful
    // map when no AuditPlots are on file yet.
    const allFeatures = mapData?.features ?? [];
    const hasAuditPlots = allFeatures.some(
      (f) => f?.properties?.layer_type === "audit_plot",
    );
    const wantedLayer: "audit_plot" | "farm_plot" = hasAuditPlots
      ? "audit_plot"
      : "farm_plot";

    const features = featuresLayerRef.current;
    if (!features) return null;

    // Walk wrappers → leaves (sub-paths with a .feature) so we can
    // tell which wrappers belong to the wanted layer.
    const visit = (lyr: L.Layer, fn: (leaf: L.Layer) => void): void => {
      if ((lyr as any).feature) {
        fn(lyr);
        return;
      }
      if (typeof (lyr as any).eachLayer === "function") {
        (lyr as any).eachLayer((child: L.Layer) => visit(child, fn));
      }
    };

    // Hide every wrapper whose feature isn't of the wanted layer
    // type. Track the removals so we can restore them later.
    const removedLayers: L.Layer[] = [];
    features.eachLayer((wrapper: L.Layer) => {
      let belongs = false;
      visit(wrapper, (leaf) => {
        const lt = (leaf as any).feature?.properties?.layer_type;
        if (lt === wantedLayer) belongs = true;
      });
      if (!belongs) removedLayers.push(wrapper);
    });
    removedLayers.forEach((lyr) => features.removeLayer(lyr));

    // Collect plot centroids + names from the surviving wrappers.
    type PlotLabel = {
      name: string;
      latlng: L.LatLng;
    };
    const plotLabels: PlotLabel[] = [];
    features.eachLayer((wrapper: L.Layer) => {
      visit(wrapper, (leaf) => {
        const props: MapFeature["properties"] | undefined =
          (leaf as any).feature?.properties;
        if (!props || props.layer_type !== wantedLayer) return;
        const name = (props.name || "").trim();
        if (!name) return;
        try {
          let centre: L.LatLng | null = null;
          if ((leaf as any).getBounds) {
            centre = (leaf as any).getBounds().getCenter();
          } else if ((leaf as any).getLatLng) {
            centre = (leaf as any).getLatLng();
          }
          if (centre) plotLabels.push({ name, latlng: centre });
        } catch {
          /* skip broken geometry */
        }
      });
    });

    // Save the view so we can restore it once the snapshot is done.
    const previousCenter = mapInstance.getCenter();
    const previousZoom = mapInstance.getZoom();

    // Tighten the framing to the plots only.
    let viewChanged = false;
    try {
      let tightBounds: L.LatLngBounds | null = null;
      features.eachLayer((wrapper: L.Layer) => {
        visit(wrapper, (leaf) => {
          try {
            let b: L.LatLngBounds | null = null;
            if ((leaf as any).getBounds) {
              b = (leaf as any).getBounds();
            } else if ((leaf as any).getLatLng) {
              const ll = (leaf as any).getLatLng();
              b = L.latLngBounds(ll, ll);
            }
            if (b) tightBounds = tightBounds ? tightBounds.extend(b) : b;
          } catch {
            /* skip */
          }
        });
      });
      if (tightBounds && (tightBounds as L.LatLngBounds).isValid()) {
        mapInstance.fitBounds(tightBounds, {
          animate: false,
          padding: [24, 24],
          maxZoom: 19,
        });
        mapInstance.invalidateSize();
        viewChanged = true;
      }
    } catch {
      /* best-effort framing */
    }

    setExportingPlotMap(true);
    try {
      // Give satellite tiles time to settle before the snapshot.
      await new Promise((r) => setTimeout(r, viewChanged ? 1000 : 500));

      const containerWidth = container.offsetWidth;
      const containerHeight = container.offsetHeight;
      const pixelRatio = 2;

      const { toPng } = await import("html-to-image");
      const dataUrl = await toPng(container, {
        cacheBust: true,
        pixelRatio,
        filter: (node: HTMLElement) => {
          const cls = node?.className?.toString?.() ?? "";
          return (
            !cls.includes("leaflet-control-zoom") &&
            !cls.includes("leaflet-control-attribution") &&
            !cls.includes("leaflet-control-layers")
          );
        },
      });

      // Load the raw map snapshot + the Cyberagropolis logo in
      // parallel. The logo is optional — if it 404s in some env we
      // still produce the snapshot, just without the brand mark.
      const loadImage = (src: string): Promise<HTMLImageElement | null> =>
        new Promise((resolve) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = () => resolve(null);
          im.src = src;
        });

      const [mapImg, logoImg] = await Promise.all([
        loadImage(dataUrl),
        loadImage("/logo_cyberagropolis.png"),
      ]);
      if (!mapImg) throw new Error("Could not decode map snapshot.");

      // ---- 1) Paint plot-name labels on the bare map snapshot ----
      const mapCanvas = document.createElement("canvas");
      mapCanvas.width = mapImg.naturalWidth;
      mapCanvas.height = mapImg.naturalHeight;
      const mapCtx = mapCanvas.getContext("2d");
      if (!mapCtx) throw new Error("No 2D context (map layer).");
      mapCtx.drawImage(mapImg, 0, 0);

      mapCtx.font =
        "600 14px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
      mapCtx.textBaseline = "middle";
      mapCtx.textAlign = "center";

      const drawRoundedRect = (
        c: CanvasRenderingContext2D,
        x: number,
        y: number,
        w: number,
        h: number,
        r: number,
      ) => {
        c.beginPath();
        c.moveTo(x + r, y);
        c.lineTo(x + w - r, y);
        c.quadraticCurveTo(x + w, y, x + w, y + r);
        c.lineTo(x + w, y + h - r);
        c.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
        c.lineTo(x + r, y + h);
        c.quadraticCurveTo(x, y + h, x, y + h - r);
        c.lineTo(x, y + r);
        c.quadraticCurveTo(x, y, x + r, y);
        c.closePath();
      };

      // Scale factor between Leaflet container coords (CSS pixels)
      // and the captured snapshot (CSS px × pixelRatio).
      const scaleX = mapCanvas.width / containerWidth;
      const scaleY = mapCanvas.height / containerHeight;
      const labelPaddingX = 8 * pixelRatio;
      const labelPaddingY = 4 * pixelRatio;
      const labelRadius = 6 * pixelRatio;
      const labelLineHeight = 16 * pixelRatio;

      mapCtx.font = `600 ${14 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;

      // Cluster labels whose pixel positions are within CLUSTER_PX of
      // each other so that two parcelas with identical (or nearly
      // identical) geometry render as one multi-line pill instead of
      // two stacked single-line pills that would mash on top of each
      // other. The cluster centroid is the mean of its members, so a
      // pair of overlapping parcels gives a label sitting exactly on
      // the shared centroid.
      const CLUSTER_PX = 30 * pixelRatio;
      type LabelCluster = {
        sumX: number;
        sumY: number;
        names: string[];
      };
      const clusters: LabelCluster[] = [];
      for (const { name, latlng } of plotLabels) {
        const pt = mapInstance.latLngToContainerPoint(latlng);
        const cx = pt.x * scaleX;
        const cy = pt.y * scaleY;
        let merged: LabelCluster | null = null;
        for (const c of clusters) {
          const ccx = c.sumX / c.names.length;
          const ccy = c.sumY / c.names.length;
          if (Math.hypot(ccx - cx, ccy - cy) < CLUSTER_PX) {
            merged = c;
            break;
          }
        }
        if (merged) {
          merged.sumX += cx;
          merged.sumY += cy;
          merged.names.push(name);
        } else {
          clusters.push({ sumX: cx, sumY: cy, names: [name] });
        }
      }

      for (const cluster of clusters) {
        const cx = cluster.sumX / cluster.names.length;
        const cy = cluster.sumY / cluster.names.length;
        // Sort names so the pill order is deterministic across exports.
        const names = [...cluster.names].sort((a, b) => a.localeCompare(b));

        // Pill width = widest line + horizontal padding; height
        // scales with line count.
        let widestLine = 0;
        for (const n of names) {
          widestLine = Math.max(widestLine, mapCtx.measureText(n).width);
        }
        const boxW = widestLine + labelPaddingX * 2;
        const boxH = labelLineHeight * names.length + labelPaddingY * 2;
        const boxX = cx - boxW / 2;
        const boxY = cy - boxH / 2;

        mapCtx.fillStyle = "rgba(255, 255, 255, 0.82)";
        drawRoundedRect(mapCtx, boxX, boxY, boxW, boxH, labelRadius);
        mapCtx.fill();
        mapCtx.lineWidth = 1 * pixelRatio;
        mapCtx.strokeStyle = "rgba(0, 0, 0, 0.35)";
        drawRoundedRect(mapCtx, boxX, boxY, boxW, boxH, labelRadius);
        mapCtx.stroke();

        mapCtx.fillStyle = "#1f1f1f";
        mapCtx.textBaseline = "middle";
        mapCtx.textAlign = "center";
        const firstLineCenterY =
          boxY + labelPaddingY + labelLineHeight / 2;
        for (let i = 0; i < names.length; i++) {
          mapCtx.fillText(
            names[i],
            cx,
            firstLineCenterY + i * labelLineHeight,
          );
        }
      }

      // ---- 2) Compose the final image: header + framed map +
      //         footer. All sizes are in *output* pixels (i.e.
      //         already scaled by pixelRatio). ----
      const headerH = 110 * pixelRatio;
      const footerH = 44 * pixelRatio;
      const sidePad = 28 * pixelRatio;
      const framePad = 6 * pixelRatio;
      const accent = "#FA8C16";
      const ink = "#1f1f1f";
      const muted = "#8c8c8c";

      const finalW = mapCanvas.width + sidePad * 2;
      const finalH = headerH + mapCanvas.height + footerH + framePad * 2;

      const out = document.createElement("canvas");
      out.width = finalW;
      out.height = finalH;
      const ctx = out.getContext("2d");
      if (!ctx) throw new Error("No 2D context (final).");

      // Background: clean white sheet.
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, finalW, finalH);

      // ---- Header: logo on the left, audit info on the right.
      const headerInnerY = 20 * pixelRatio;
      const logoTargetH = 38 * pixelRatio;
      let textStartX = sidePad;
      if (logoImg) {
        const ratio = logoImg.naturalHeight
          ? logoImg.naturalWidth / logoImg.naturalHeight
          : 1;
        const logoW = logoTargetH * ratio;
        ctx.drawImage(logoImg, sidePad, headerInnerY, logoW, logoTargetH);
        textStartX = sidePad + logoW + 20 * pixelRatio;
      }

      ctx.textBaseline = "top";
      ctx.textAlign = "left";

      // Title — reference + audit label.
      ctx.fillStyle = ink;
      ctx.font = `700 ${22 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      const title = `${t("audit.title", "Auditoría")}${auditReference ? ` · ${auditReference}` : ""}`;
      ctx.fillText(title, textStartX, headerInnerY);

      // Sub-line: producer · farm.
      ctx.fillStyle = muted;
      ctx.font = `500 ${14 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      const subParts: string[] = [];
      if (producerName) {
        subParts.push(
          `${t("audit.producer", "Productor")}: ${producerName}`,
        );
      }
      if (farmName) {
        subParts.push(`${t("audit.farm", "Finca")}: ${farmName}`);
      }
      const subLine = subParts.join("   ·   ");
      ctx.fillText(subLine, textStartX, headerInnerY + 32 * pixelRatio);

      // What this image shows — small caption.
      ctx.fillStyle = muted;
      ctx.font = `400 ${12 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.fillText(
        wantedLayer === "audit_plot"
          ? t("audit.export_caption_plots", "Mapa de parcelas auditadas")
          : t(
              "audit.export_caption_farm",
              "Mapa de la geometría de la finca",
            ),
        textStartX,
        headerInnerY + 56 * pixelRatio,
      );

      // ---- Accent divider between header and map.
      ctx.fillStyle = accent;
      ctx.fillRect(
        sidePad,
        headerH - 4 * pixelRatio,
        finalW - sidePad * 2,
        2 * pixelRatio,
      );

      // ---- Framed map.
      const frameX = sidePad - framePad;
      const frameY = headerH;
      const frameW = mapCanvas.width + framePad * 2;
      const frameH = mapCanvas.height + framePad * 2;
      ctx.lineWidth = 1 * pixelRatio;
      ctx.strokeStyle = "rgba(0, 0, 0, 0.18)";
      drawRoundedRect(ctx, frameX, frameY, frameW, frameH, 10 * pixelRatio);
      ctx.stroke();

      // Subtle shadow under the frame to lift the map off the page.
      ctx.save();
      ctx.shadowColor = "rgba(0, 0, 0, 0.10)";
      ctx.shadowBlur = 12 * pixelRatio;
      ctx.shadowOffsetY = 3 * pixelRatio;
      ctx.drawImage(
        mapCanvas,
        sidePad,
        headerH + framePad,
        mapCanvas.width,
        mapCanvas.height,
      );
      ctx.restore();

      // ---- Footer: brand + ISO date, right-aligned.
      const footerY = headerH + mapCanvas.height + framePad * 2 + 14 * pixelRatio;
      ctx.fillStyle = muted;
      ctx.font = `500 ${12 * pixelRatio}px -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
      ctx.textAlign = "left";
      ctx.fillText(
        t("audit.export_footer_brand", "Generado por Cyberagropolis"),
        sidePad,
        footerY,
      );
      const today = new Date();
      const dateStr = today.toLocaleDateString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
      ctx.textAlign = "right";
      ctx.fillText(dateStr, finalW - sidePad, footerY);

      // Materialise both a Blob (for ZIP bundling) and a dataURL
      // (for `<a download>` flow + previews). Toolbar download is
      // wired in `exportPlotMapPng` below.
      const finalDataUrl = out.toDataURL("image/png");
      const blob: Blob = await new Promise<Blob>((resolve, reject) => {
        out.toBlob((b) => {
          if (b) resolve(b);
          else reject(new Error("Canvas toBlob returned null"));
        }, "image/png");
      });
      const fileName = `mapa-parcelas-${auditReference || auditId || "auditoria"}.png`;
      return { blob, dataUrl: finalDataUrl, fileName };
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[AuditMapCard] plot-map export failed", e);
      return null;
    } finally {
      // Restore visibility + view exactly as the user left them.
      removedLayers.forEach((lyr) => features.addLayer(lyr));
      if (viewChanged && isMapAlive(mapInstance)) {
        try {
          mapInstance.setView(previousCenter, previousZoom, {
            animate: false,
          });
        } catch {
          /* best-effort restore */
        }
      }
    }
  };

  // Toolbar handler — wraps the composer with the loading flag, the
  // toast feedback and the `<a download>` step. Same UX as before;
  // only the composer now lives somewhere reusable.
  const exportPlotMapPng = async () => {
    if (!mapInstance) return;
    if (!featuresLayerRef.current) {
      message.warning(
        t("audit.map_export_no_features", "Sin parcelas que exportar."),
      );
      return;
    }
    setExportingPlotMap(true);
    try {
      const result = await composePlotMapImage();
      if (!result) {
        message.error(
          t(
            "audit.map_export_error",
            "No se pudo generar la imagen del mapa.",
          ),
        );
        return;
      }
      const link = document.createElement("a");
      link.href = result.dataUrl;
      link.download = result.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      message.success(
        t("audit.map_export_success", "Mapa descargado."),
      );
    } finally {
      setExportingPlotMap(false);
    }
  };

  // Exposed to the audit-show page so the closure-report flow can
  // grab a real Leaflet snapshot (satellite tiles + every visible
  // feature labelled with its `name`) and embed it in the PDF.
  useImperativeHandle(
    ref,
    () => ({
      async captureForReport(mode: MapCaptureMode = "main") {
        if (!mapInstance) return null;
        const container = mapInstance.getContainer() as HTMLElement | null;
        if (!container) return null;

        // Per-mode policy: which layer types stay visible and which
        // ones receive a label pill in the PDF.
        const VISIBLE: Record<MapCaptureMode, Set<string>> = {
          main: new Set([
            "audit_plot",
            "farm_plot",
            "adjacent_crop",
            "control_point",
          ]),
          samples: new Set(["audit_plot", "sample"]),
        };
        const LABELLED: Record<MapCaptureMode, Set<string>> = {
          // Main map labels every visible feature.
          main: new Set([
            "audit_plot",
            "farm_plot",
            "adjacent_crop",
            "control_point",
          ]),
          // Samples map only labels the sample flags — the audit
          // plots stay as bare polygons so the layout reads as a
          // sample-distribution map.
          samples: new Set(["sample"]),
        };
        // Prefix each feature's `name` with its layer type so the
        // PDF reads as "Parcela P-01" / "Punto de control PC-02" /
        // etc. — the bare name on its own is ambiguous on a map that
        // mixes plots, control points and adjacent crops.
        const LABEL_PREFIX: Partial<
          Record<MapFeature["properties"]["layer_type"], string>
        > = {
          audit_plot: "Parcela ",
          farm_plot: "Parcela finca ",
          adjacent_crop: "Colindante ",
          control_point: "PC ",
          route: "Ruta ",
          sample: "WP ",
        };
        // Which property to read for the label text per layer type.
        // Adjacent crops use the variety name (the auditor cares
        // about *what* is growing next door, not the crop's id);
        // samples are labelled by waypoint; everything else uses
        // the feature `name`.
        const labelTextFor = (
          props: MapFeature["properties"],
        ): string | undefined => {
          if (props.layer_type === "adjacent_crop") {
            return props.variety || props.species || props.name;
          }
          if (props.layer_type === "sample") {
            return props.waypoint || props.name;
          }
          return props.name;
        };

        // `featuresLayerRef.current` is a FeatureGroup that contains
        // one L.geoJSON wrapper per feature; each wrapper is itself a
        // LayerGroup whose leaf Path/Marker carries the `.feature`
        // property. A flat `eachLayer` only gets the wrappers (no
        // `.feature`), so we recurse with `visit` to reach the
        // leaves.
        const visit = (lyr: L.Layer, fn: (leaf: L.Layer) => void): void => {
          if ((lyr as any).feature) {
            fn(lyr);
            return;
          }
          if (typeof (lyr as any).eachLayer === "function") {
            (lyr as any).eachLayer((child: L.Layer) => visit(child, fn));
          }
        };

        // Hide layer-types that don't belong in this capture mode
        // (e.g. for the samples map we want only audit plots +
        // samples; for the main map we want everything except
        // samples). Each removed wrapper is pushed onto
        // `removedLayers` so the `finally` block can restore them.
        const removedLayers: L.Layer[] = [];
        const features = featuresLayerRef.current;
        const visibleSet = VISIBLE[mode];
        if (features) {
          features.eachLayer((wrapper: L.Layer) => {
            let shouldHide = false;
            visit(wrapper, (leaf) => {
              const lt = (leaf as any).feature?.properties?.layer_type;
              if (lt && !visibleSet.has(lt)) shouldHide = true;
            });
            if (shouldHide) removedLayers.push(wrapper);
          });
          removedLayers.forEach((lyr) => features.removeLayer(lyr));
        }

        // For the samples map we want the parcelas tightly framed
        // (the colindantes/PCs frame the main map too widely once
        // you hide them). Save the current view, refit to the union
        // of `audit_plot` + `sample` bounds — colindantes are
        // intentionally excluded — then restore in the cleanup
        // block. Done *before* the centroid collection because
        // `latLngToContainerPoint` depends on the current view.
        const previousCenter = mapInstance.getCenter();
        const previousZoom = mapInstance.getZoom();
        let viewChanged = false;
        if (mode === "samples" && features) {
          const tightSet = new Set(["audit_plot", "sample"]);
          let tightBounds: L.LatLngBounds | null = null;
          features.eachLayer((wrapper: L.Layer) => {
            visit(wrapper, (leaf) => {
              const lt = (leaf as any).feature?.properties?.layer_type;
              if (!lt || !tightSet.has(lt)) return;
              try {
                let b: L.LatLngBounds | null = null;
                if ((leaf as any).getBounds) {
                  b = (leaf as any).getBounds();
                } else if ((leaf as any).getLatLng) {
                  const ll = (leaf as any).getLatLng();
                  b = L.latLngBounds(ll, ll);
                }
                if (b) {
                  tightBounds = tightBounds ? tightBounds.extend(b) : b;
                }
              } catch {
                /* ignore broken geometry */
              }
            });
          });
          if (tightBounds && (tightBounds as L.LatLngBounds).isValid()) {
            // Whatever zoom `fitBounds` picks is what we keep —
            // bumping it by 1 cropped some samples out of view.
            mapInstance.fitBounds(tightBounds, {
              animate: false,
              padding: [24, 24],
              maxZoom: 19,
            });
            // Force Leaflet to flush the new view synchronously so
            // the subsequent `latLngToContainerPoint` calls land in
            // the right pixel space.
            mapInstance.invalidateSize();
            viewChanged = true;
          }
        }

        // Collect each labelled feature's centroid in container
        // pixel coordinates. Runs *after* the optional fitBounds so
        // pixel positions match the captured view.
        const labelledSet = LABELLED[mode];
        const captureLabels: MapCaptureLabel[] = [];
        if (features) {
          features.eachLayer((wrapper: L.Layer) => {
            visit(wrapper, (leaf) => {
              const f = (leaf as any).feature;
              const props: MapFeature["properties"] | undefined = f?.properties;
              const lt = props?.layer_type;
              if (!props || !lt || !labelledSet.has(lt)) return;
              const text = labelTextFor(props);
              if (!text) return;
              let centre: L.LatLng | null = null;
              try {
                if ((leaf as any).getBounds) {
                  centre = (leaf as any).getBounds().getCenter();
                } else if ((leaf as any).getLatLng) {
                  centre = (leaf as any).getLatLng();
                }
              } catch {
                centre = null;
              }
              if (!centre) return;
              const pt = mapInstance.latLngToContainerPoint(centre);
              captureLabels.push({
                name: `${LABEL_PREFIX[lt] ?? ""}${text}`,
                layerType: lt,
                x: pt.x,
                y: pt.y,
              });
            });
          });
        }
        // eslint-disable-next-line no-console
        console.debug(
          `[AuditMapCard] mode=${mode} collected ${captureLabels.length} feature centroids`,
          captureLabels,
        );

        // Record container dimensions before screenshotting — the
        // PDF builder uses them to scale label positions and to
        // size the placed image at the correct aspect ratio.
        const containerWidth = container.offsetWidth;
        const containerHeight = container.offsetHeight;

        // Give satellite tiles time to settle after the (possible)
        // zoom change before the snapshot. Slightly longer when we
        // refit the view because the higher zoom triggers new tile
        // downloads.
        await new Promise((r) =>
          setTimeout(r, viewChanged ? 900 : 400),
        );

        try {
          // JPEG en vez de PNG: las capturas de Leaflet con ortofoto
          // satelital + polígonos están dominadas por textura
          // fotográfica continua, donde el PNG era 5-10x más pesado
          // que un JPEG de calidad equivalente. En prod estábamos en
          // ~17 MB de media por PDF — casi todo era este PNG sin
          // compresión — chocando con el client_max_body_size de 20M
          // de nginx en finalize. quality 0.82 da un JPEG visualmente
          // indistinguible y ~6-8x más pequeño.
          const { toJpeg } = await import("html-to-image");
          const dataUrl = await toJpeg(container, {
            cacheBust: true,
            pixelRatio: 2,
            quality: 0.82,
            // Skip Leaflet's zoom-control and attribution from the
            // snapshot — they clutter the PDF.
            filter: (node: HTMLElement) => {
              const cls = node?.className?.toString?.() ?? "";
              return (
                !cls.includes("leaflet-control-zoom") &&
                !cls.includes("leaflet-control-attribution")
              );
            },
          });
          return {
            dataUrl,
            containerWidth,
            containerHeight,
            labels: captureLabels,
          };
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn("[AuditMapCard] map capture failed", e);
          return null;
        } finally {
          if (features) {
            // Restore every layer we hid for this capture mode so
            // the live map ends in the same state we found it.
            removedLayers.forEach((lyr) => features.addLayer(lyr));
          }
          if (viewChanged && isMapAlive(mapInstance)) {
            try {
              mapInstance.setView(previousCenter, previousZoom, {
                animate: false,
              });
            } catch {
              /* ignore — best-effort restore */
            }
          }
        }
      },
      composePlotMapImage,
    }),
    [mapInstance, composePlotMapImage],
  );
  // Leaflet.draw control ref so we can attach/detach when switching
  // create modes without leaking previous handlers.
  const drawControlRef = useRef<L.Control.Draw | null>(null);
  const drawnGroupRef = useRef<L.FeatureGroup | null>(null);
  // Tell Leaflet to recompute its size whenever the parent gives the
  // map a new `mapHeight` (e.g. when show.tsx matches the left
  // column's height via ResizeObserver). Without this the map's tile
  // layer keeps its initial canvas size and the new space stays gray.
  useEffect(() => {
    if (!isMapAlive(mapInstance)) return;
    // Two ticks: one immediately and one on next frame, so the DOM has
    // had time to apply the new height.
    try { mapInstance.invalidateSize(); } catch { /* dead map */ }
    const raf = requestAnimationFrame(() => {
      if (!isMapAlive(mapInstance)) return;
      try { mapInstance.invalidateSize(); } catch { /* dead map */ }
    });
    return () => cancelAnimationFrame(raf);
  }, [mapInstance, mapHeight]);

  // Hidden file input for the GPX upload flow.
  const gpxInputRef = useRef<HTMLInputElement | null>(null);

  const fetchMapData = useCallback(async () => {
    if (!auditId) return;
    setLoading(true);
    try {
      const res = await apiClient.get<MapData>(`/audits/${auditId}/map_data/`);
      setMapData(res.data);
    } catch {
      setMapData(null);
    } finally {
      setLoading(false);
    }
  }, [auditId]);

  useEffect(() => {
    fetchMapData();
    // `refreshKey` is intentionally part of the dep list — any change
    // (a bump from the parent after a delete) re-runs the fetch and
    // the map drops/loads features accordingly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchMapData, refreshKey]);

  // "Map is ready" signal — fires once (per audit) when both the
  // Leaflet instance is up and the map data has loaded. Used by the
  // bulk plot-maps exporter to know when it can call
  // `composePlotMapImage()` and move on to the next audit without a
  // fixed timeout. Guarded by a ref so refreshKey bumps or unrelated
  // re-renders don't retrigger it.
  const mapReadyFiredRef = useRef(false);
  useEffect(() => {
    if (mapReadyFiredRef.current) return;
    if (!mapInstance || !mapData) return;
    mapReadyFiredRef.current = true;
    onMapReady?.();
  }, [mapInstance, mapData, onMapReady]);

  // Fetch de la capa "Vecinos protegidos". Solo se dispara cuando
  // el usuario la activa desde la leyenda — evita pegarle a
  // /nearby_plots/ en cada apertura del mapa (query PostGIS extra
  // que no aporta al 90% de los auditores). Al cambiar el radio se
  // re-fetchea con el nuevo valor.
  const fetchNearby = useCallback(async () => {
    if (!auditId) return;
    setNearbyLoading(true);
    try {
      const res = await apiClient.get<{
        features?: MapFeature[];
        meta?: { count?: number };
      }>(`/audits/${auditId}/nearby_plots/`, {
        params: { radius_m: nearbyRadiusKm * 1000 },
      });
      setNearbyFeatures(res.data?.features ?? []);
    } catch {
      setNearbyFeatures([]);
    } finally {
      setNearbyLoading(false);
    }
  }, [auditId, nearbyRadiusKm]);

  useEffect(() => {
    if (layerVisible.nearby_protected_plot) {
      void fetchNearby();
    } else {
      // Al apagar la capa vaciamos el buffer local — no tiene sentido
      // seguir pintando (o dejando en memoria) las features que el
      // auditor ha decidido no ver.
      setNearbyFeatures([]);
    }
  }, [layerVisible.nearby_protected_plot, fetchNearby]);

  // Merge de map_data + nearby en un único FeatureCollection para
  // que el resto del componente (renderizado de features, filtros
  // por layer_type, leyenda) siga siendo agnóstico del origen.
  // Memoizamos para no romper la referencia estable de `mapData`
  // que la leyenda / renderizado usan como dep.
  const mergedMapData: MapData | null = useMemo(() => {
    if (!mapData && nearbyFeatures.length === 0) return null;
    const base = mapData?.features ?? [];
    return {
      type: "FeatureCollection",
      features: [...base, ...nearbyFeatures],
    };
  }, [mapData, nearbyFeatures]);

  // Delete dispatcher per layer type. Returns true on success so the
  // caller can refresh the map.
  const deleteFeature = useCallback(
    async (layerType: string, id: string): Promise<boolean> => {
      try {
        if (layerType === "audit_plot") {
          await auditPlotService.deletePlot(id, auditId);
        } else if (layerType === "control_point") {
          await controlPointService.deleteControlPoint(id);
        } else if (layerType === "adjacent_crop") {
          await adjacentCropService.deleteAdjacentCrop(id);
        } else {
          return false;
        }
        message.success(t("common.delete_success", "Eliminado correctamente"));
        return true;
      } catch (err: any) {
        message.error(
          err?.message ?? t("common.delete_error", "Error al eliminar"),
        );
        return false;
      }
    },
    [auditId, t],
  );

  // Wire up Leaflet.draw whenever the user selects a non-"none"
  // creation mode. Polygon for parcel/adjacent crop, marker for
  // control point. When the user completes the shape, we navigate to
  // the existing create page with the drawn geometry in router state
  // — that page already supports being preloaded with geometry.
  // Trigger the file picker when the user picks the GPX option.
  // Reset the value first so re-selecting the same file fires `change`.
  useEffect(() => {
    if (createMode !== "plot-gpx") return;
    const input = gpxInputRef.current;
    if (!input) return;
    input.value = "";
    input.click();
  }, [createMode]);

  const handleGpxFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setCreateMode("none");
      return;
    }
    // Detectamos el flavor por la extensión para dar un error más
    // preciso si el parseo falla (KML vs GPX). El parser mismo detecta
    // por el elemento raíz, así que la extensión sólo se usa para el
    // texto del mensaje.
    const isKml = /\.kml$/i.test(file.name);
    try {
      const text = await file.text();
      const geometry = parseGpxOrKmlToPolygon(text);
      if (!geometry) {
        message.error(
          isKml
            ? t(
                "audit.kml_parse_error",
                "No se pudo extraer una parcela del archivo KML. Debe contener un Polygon, un LineString o al menos 3 Placemarks Point.",
              )
            : t(
                "audit.gpx_parse_error",
                "No se pudo extraer una parcela del archivo GPX (se necesitan al menos 3 puntos de track o waypoints).",
              ),
        );
        setCreateMode("none");
        return;
      }
      navigate(`/audits/${auditId}/plots/create`, {
        state: { initialGeometry: geometry },
      });
      setCreateMode("none");
    } catch {
      message.error(
        isKml
          ? t("audit.kml_read_error", "No se pudo leer el archivo KML.")
          : t("audit.gpx_read_error", "No se pudo leer el archivo GPX."),
      );
      setCreateMode("none");
    }
  };

  useEffect(() => {
    if (!mapInstance || createMode === "none" || createMode === "plot-gpx") return;

    // Drawn shapes land on this feature group so we can clean them
    // up — we won't persist them on the map directly.
    const drawnItems = new L.FeatureGroup();
    drawnGroupRef.current = drawnItems;
    mapInstance.addLayer(drawnItems);

    const isPoint = createMode === "control-point";
    // Polygon stroke/fill color: purple for adjacent crops, orange
    // otherwise (matches the per-feature styling on the audit map).
    const polygonColor = createMode === "adjacent-crop" ? "#722ed1" : "#ff8c00";
    const drawControl = new L.Control.Draw({
      position: "topright",
      draw: {
        polyline: false,
        rectangle: false,
        circle: false,
        circlemarker: false,
        polygon: isPoint
          ? false
          : {
              allowIntersection: false,
              showArea: false,
              shapeOptions: {
                color: polygonColor,
                weight: 3,
                fillOpacity: 0.25,
              },
            },
        marker: isPoint ? {} : false,
      },
      edit: { featureGroup: drawnItems, edit: false, remove: false },
    });
    drawControlRef.current = drawControl;
    mapInstance.addControl(drawControl);

    const onCreated = (event: any) => {
      const layer = event.layer as L.Layer;
      const geo = (layer as any).toGeoJSON?.();
      const geometry: GeoJSON.Geometry | null = geo?.geometry ?? null;
      if (!geometry) {
        message.error(
          t("audit.create_no_geometry", "No se pudo capturar la geometría"),
        );
        return;
      }
      const state = { initialGeometry: geometry };
      if (createMode === "plot") {
        navigate(`/audits/${auditId}/plots/create`, { state });
      } else if (createMode === "control-point") {
        navigate(`/control-points/create?auditId=${auditId}`, { state });
      } else if (createMode === "adjacent-crop") {
        navigate(`/adjacent-crops/create?auditId=${auditId}`, { state });
      }
      // Reset the selector so the map returns to view mode for the
      // (brief) moment between navigations.
      setCreateMode("none");
    };

    mapInstance.on(L.Draw.Event.CREATED, onCreated);

    return () => {
      mapInstance.off(L.Draw.Event.CREATED, onCreated);
      if (drawControlRef.current) {
        mapInstance.removeControl(drawControlRef.current);
        drawControlRef.current = null;
      }
      if (drawnGroupRef.current) {
        mapInstance.removeLayer(drawnGroupRef.current);
        drawnGroupRef.current = null;
      }
    };
  }, [mapInstance, createMode, auditId, navigate, t]);

  useEffect(() => {
    if (!mapInstance || !mergedMapData) return;

    // Tear down anything we drew on a previous render so styles /
    // popups don't pile up.
    if (featuresLayerRef.current) {
      mapInstance.removeLayer(featuresLayerRef.current);
      featuresLayerRef.current = null;
    }
    // Drop any pending highlight reference — the layer it pointed at
    // is gone after the removal above.
    highlightedRef.current = null;

    // Restore the original style of whatever was last highlighted.
    // Used both on popup close and when the user clicks a different
    // feature (Leaflet auto-closes the prior popup → popupclose
    // fires → we reset).
    const clearHighlight = () => {
      const h = highlightedRef.current;
      if (!h) return;
      try {
        h.layer.setStyle?.(h.baseStyle);
      } catch {
        /* ignore — layer may already have been torn down */
      }
      highlightedRef.current = null;
    };

    // ---- Overlap chooser ------------------------------------------
    // When the user clicks a spot where several polygons are stacked
    // (two parcelas with identical geometry, a CP fully inside a
    // parcela, etc.) Leaflet only fires the click on the topmost
    // path — the rest become unreachable. We detect every polygon
    // that contains the click point and, if more than one, swap the
    // topmost's popup for a chooser listing them all. Picking an
    // entry refires `click` on that specific layer so it gets its
    // own popup + highlight as if the user had reached it directly.
    //
    // Note: this lives inside the per-feature click handler instead
    // of on a map-level click listener because Leaflet's bindPopup
    // calls `L.DomEvent.stop(e)` on the original DOM event, which
    // sets `_stopped` and prevents the map.click from ever firing.
    const pointInRing = (pt: L.LatLng, ring: L.LatLng[]): boolean => {
      const x = pt.lng;
      const y = pt.lat;
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const xi = ring[i].lng;
        const yi = ring[i].lat;
        const xj = ring[j].lng;
        const yj = ring[j].lat;
        const intersect =
          yi > y !== yj > y &&
          x < ((xj - xi) * (y - yi)) / (yj - yi || 1e-12) + xi;
        if (intersect) inside = !inside;
      }
      return inside;
    };
    const pointInLatLngs = (pt: L.LatLng, latlngs: any): boolean => {
      if (!Array.isArray(latlngs) || latlngs.length === 0) return false;
      // Leaflet returns LatLng[] for simple polygons, LatLng[][] for
      // polygons with holes / multipolygons, and LatLng[][][] for
      // multipolygons with holes. Recurse until we hit a LatLng[].
      if (latlngs[0] && typeof (latlngs[0] as L.LatLng).lat === "number") {
        return pointInRing(pt, latlngs as L.LatLng[]);
      }
      for (const sub of latlngs) {
        if (pointInLatLngs(pt, sub)) return true;
      }
      return false;
    };
    type OverlapMatch = { layer: any; props: MapFeature["properties"] };
    const findOverlappingPolygons = (latLng: L.LatLng): OverlapMatch[] => {
      const matches: OverlapMatch[] = [];
      const fg = featuresLayerRef.current;
      if (!fg) return matches;
      const visit = (lyr: L.Layer): void => {
        const any = lyr as any;
        if (any.feature) {
          if (
            typeof any.getBounds === "function" &&
            typeof any.getLatLngs === "function"
          ) {
            try {
              const bounds: L.LatLngBounds = any.getBounds();
              if (!bounds.contains(latLng)) return;
              const ring = any.getLatLngs();
              if (pointInLatLngs(latLng, ring)) {
                matches.push({ layer: lyr, props: any.feature.properties });
              }
            } catch {
              /* skip malformed polygon */
            }
          }
          return;
        }
        if (typeof any.eachLayer === "function") {
          any.eachLayer(visit);
        }
      };
      fg.eachLayer(visit);
      return matches;
    };

    // Forward declarations resolved later in this same effect; we
    // only reference them at click time (closure resolves at call
    // time, not definition).
    let escapeFn: (s: string | undefined | null) => string = (s) =>
      String(s ?? "");
    let titleForFn: (lt: MapFeature["properties"]["layer_type"]) => string =
      (lt) => String(lt);
    const showOverlapChooser = (latLng: L.LatLng, matches: OverlapMatch[]) => {
      mapInstance.closePopup();
      clearHighlight();

      const items = matches
        .map((m, i) => {
          const name = escapeFn(m.props.name || "—");
          const title = escapeFn(titleForFn(m.props.layer_type));
          return `
            <button
              type="button"
              data-overlap-idx="${i}"
              style="text-align:left;padding:8px 10px;border:1px solid #d9d9d9;background:#fff;border-radius:4px;cursor:pointer;font-size:12px;width:100%;display:flex;flex-direction:column;gap:2px;line-height:1.2;">
              <span style="color:#1f1f1f;font-weight:600;">${title}</span>
              <span style="color:#595959;">${name}</span>
            </button>`;
        })
        .join("");

      const html = `
        <div style="font-family: Arial, sans-serif; min-width: 240px;">
          <strong style="font-size:13px;color:#000;">
            ${escapeFn(
              t(
                "audit.overlap_chooser_title",
                "{{n}} elementos en este punto",
                { n: matches.length },
              ),
            )}
          </strong>
          <div style="font-size:11px;color:#8c8c8c;margin-top:2px;">
            ${escapeFn(
              t(
                "audit.overlap_chooser_help",
                "Selecciona la geometría que quieres ver.",
              ),
            )}
          </div>
          <hr style="margin:8px 0;border:none;border-top:1px solid #eee;"/>
          <div style="display:flex;flex-direction:column;gap:6px;">${items}</div>
        </div>`;

      const chooser = L.popup({
        closeButton: true,
        autoPan: true,
        maxWidth: 320,
      })
        .setLatLng(latLng)
        .setContent(html)
        .openOn(mapInstance);

      setTimeout(() => {
        const el = chooser.getElement();
        if (!el) return;
        el.querySelectorAll<HTMLButtonElement>("[data-overlap-idx]").forEach(
          (btn) => {
            btn.addEventListener("click", () => {
              const idx = Number(btn.dataset.overlapIdx || "0");
              const m = matches[idx];
              if (!m) return;
              mapInstance.closePopup();

              // Highlight the chosen leaf directly. We can't simply
              // re-fire `click` on it — our highlight handler is
              // bound to the wrapper L.GeoJSON, not the leaf, so
              // firing `click` here would only run bindPopup's
              // auto-open and skip the yellow outline. We apply
              // the same style overrides + bringToFront + ref the
              // wrapper handler would have, but against the leaf
              // itself.
              clearHighlight();
              const base: L.PathOptions =
                (m.layer as any)._auditMapBaseStyle ?? {};
              try {
                m.layer.setStyle?.({
                  ...base,
                  color: "#fadb14",
                  weight: 5,
                  dashArray: undefined,
                  fillOpacity: Math.min(
                    0.55,
                    (base.fillOpacity ?? 0.3) + 0.2,
                  ),
                });
                m.layer.bringToFront?.();
                highlightedRef.current = {
                  layer: m.layer,
                  baseStyle: base,
                };
              } catch {
                /* ignore — layer may be transient */
              }

              // Open the chosen feature's popup at the click point
              // (the leaf has its own bindPopup so this resolves
              // to that feature's content).
              try {
                m.layer.openPopup?.(latLng);
              } catch {
                /* ignore */
              }
            });
          },
        );
      }, 0);
    };

    const features = mergedMapData.features ?? [];
    if (features.length === 0) return;

    const layer = L.featureGroup();

    // The underlying farm-plot boundary used to read as a faint dashed
    // gray line; on satellite/light basemaps it disappeared. Bumped
    // weight + switched to a strong teal so it stays legible without
    // clashing with the audit-plot orange or the other layer colours.
    const farmStyle: L.PathOptions = {
      color: "#08979c",
      weight: 3,
      dashArray: "8 6",
      fillColor: "#08979c",
      fillOpacity: 0.08,
    };
    // Paleta alineada con el KML descargable (`audit_kml.py`) para que
    // el mapa web y el .kml se vean iguales. Fill opacity 0.6 (los
    // polígonos del KML usan alfa 0x99 ≈ 60%).
    //
    // - Parcelas auditadas: naranja pastel #FFCC80 (Material 200).
    // - Ruta del auditor: magenta #E040FB (destaca sobre satélite).
    // - Puntos de control abiertos: naranja intenso #FF9800 (M-500).
    // - Puntos de control cerrados: gris #9E9E9E.
    // - Colindantes abiertos: azul #42A5F5 (M-Blue 400).
    // - Colindantes cerrados: gris.
    const auditStyle: L.PathOptions = {
      color: "#FFCC80",
      weight: 2,
      fillColor: "#FFCC80",
      fillOpacity: 0.6,
    };
    const adjacentCropStyle: L.PathOptions = {
      color: "#42A5F5",
      weight: 2,
      fillColor: "#42A5F5",
      fillOpacity: 0.6,
    };
    // Colindante cerrado en ESTA auditoría — gris para diferenciarlo
    // del abierto (mismo criterio que el KML `_LINE_AC_CLOSED`).
    const adjacentCropClosedHereStyle: L.PathOptions = {
      color: "#9E9E9E",
      weight: 2,
      fillColor: "#9E9E9E",
      fillOpacity: 0.6,
      dashArray: "6 4",
    };
    // Puntos de control abiertos → naranja intenso; cerrados → gris.
    const controlPointStyle: L.PathOptions = {
      color: "#FF9800",
      weight: 2,
      fillColor: "#FF9800",
      fillOpacity: 0.6,
    };
    const controlPointClosedHereStyle: L.PathOptions = {
      color: "#9E9E9E",
      weight: 2,
      fillColor: "#9E9E9E",
      fillOpacity: 0.6,
      dashArray: "6 4",
    };
    // Ruta del auditor — magenta (mismo que `_LINE_ROUTE` del KML).
    const routeStyle: L.PathOptions = {
      color: "#E040FB",
      weight: 4,
      opacity: 0.85,
      fill: false,
    };
    // Parcelas vecinas de variedad protegida — púrpura oscuro con
    // trama diagonal. El fill semi-transparente permite ver el
    // basemap por debajo (SVG no soporta patrones inline en
    // L.PathOptions, así que combinamos color+fillOpacity para
    // aproximar visualmente el estilo rayado del swatch).
    const nearbyProtectedStyle: L.PathOptions = {
      color: "#531dab",
      weight: 2,
      dashArray: "4 3",
      fillColor: "#531dab",
      fillOpacity: 0.18,
    };
    // Capa de referencia catastral: parcelas licenciadas (Plot vía
    // contrato). Se renderiza bajo las AuditPlot para no taparlas;
    // el orden lo fija Leaflet respetando el orden de adición al
    // FeatureGroup.
    const licensedPlotStyle: L.PathOptions = {
      color: "#1e40af",
      weight: 1.5,
      fillColor: "#2563eb",
      fillOpacity: 0.12,
    };

    const fmt = (n: number | null | undefined, digits = 2) =>
      n == null ? "-" : Number(n).toFixed(digits);
    const escape = (s: string | undefined | null) =>
      String(s ?? "").replace(/[&<>"']/g, (c) =>
        ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" } as Record<string, string>)[c]!,
      );

    const statusTag = (status?: string) => {
      if (!status) return "";
      const color =
        status === "open" || status === "abierto" ? "#1677ff" : "#52c41a";
      return `<span style="background:${color};color:#fff;padding:1px 8px;border-radius:10px;font-size:11px;">${escape(status)}</span>`;
    };

    const rowsFor = (
      props: MapFeature["properties"],
    ): string[] => {
      const rows: string[] = [];
      const push = (label: string, value: string | number | undefined | null) => {
        if (value == null || value === "") return;
        rows.push(`<strong>${label}:</strong> ${escape(String(value))}`);
      };
      switch (props.layer_type) {
        case "audit_plot": {
          push(t("audit.plot_name", "Nombre Parcela"), props.name);
          push(t("audit.pattern", "Patrón"), props.pattern);
          push(t("audit.surface_ha", "Superficie (ha)"), fmt(props.surface));
          if (props.plantation_frame_column || props.plantation_frame_row) {
            push(
              t("audit.plantation_frame", "Marco Plantación"),
              `${props.plantation_frame_row ?? 0} x ${props.plantation_frame_column ?? 0}`,
            );
          }
          push(t("audit.num_plants", "Nº Plantas"), props.plants);
          push(
            t("audit.estimated_production", "Aforo Estimado (kg)"),
            fmt(props.kg_plot),
          );
          push(t("audit.photos", "Fotos"), props.photo_count);
          break;
        }
        case "farm_plot": {
          push(t("audit.plot_name", "Nombre"), props.name);
          push(t("audit.surface_ha", "Superficie (ha)"), fmt(props.surface));
          break;
        }
        case "licensed_plot": {
          // Sólo lectura: identifica la parcela del catastro
          // (polígono/parcela/recinto) y su superficie. Sin acciones
          // editar/borrar.
          push(t("audit.plot_name", "Nombre"), props.name);
          push(t("audit.surface_ha", "Superficie (ha)"), fmt(props.surface));
          break;
        }
        case "adjacent_crop": {
          push(t("adjacent_crops.field.species", "Especie"), props.species);
          push(t("adjacent_crops.field.variety", "Variedad"), props.variety);
          push(
            t("adjacent_crops.field.surface", "Superficie (ha)"),
            fmt(props.total_estimated_surface),
          );
          push(
            t("adjacent_crops.field.detection_date", "Fecha Detección"),
            props.detection_date,
          );
          if (props.status) {
            rows.push(
              `<strong>${t("adjacent_crops.field.status", "Estado")}:</strong> ${statusTag(props.status)}`,
            );
          }
          break;
        }
        case "control_point": {
          // Nombre del CP arriba — permite identificarlo con la lista
          // "Puntos de control" del tab (donde también se muestra el
          // nombre). Sin esto el popup solo dice motivo/estado/etc y
          // el admin no sabía cuál era cuál al comparar con la lista.
          push(t("controlPoint.name", "Nombre"), props.name);
          push(t("controlPoint.reason", "Motivo"), props.reason);
          if (props.status) {
            rows.push(
              `<strong>${t("controlPoint.status.title", "Estado")}:</strong> ${statusTag(props.status)}`,
            );
          }
          push(
            t("controlPoint.estimatedSurface", "Superficie (ha)"),
            fmt(props.total_estimated_surface),
          );
          push(
            t("controlPoint.detectionDate", "Fecha Detección"),
            props.detection_date,
          );
          push(t("controlPoint.reviewCount", "Revisiones"), props.review_count);
          push(t("controlPoint.sampleCount", "Nº Muestras"), props.sample_count);
          push(t("controlPoint.photos", "Fotos"), props.photo_count);
          break;
        }
        case "route": {
          push(t("route.auditor", "Auditor"), props.auditor);
          push(t("route.date", "Fecha"), props.date);
          if (props.distance != null) {
            push(
              t("route.distance", "Distancia (km)"),
              fmt(Number(props.distance)),
            );
          }
          break;
        }
        case "nearby_protected_plot": {
          // Anonimizado a propósito: NO se pinta productor ni finca.
          // El auditor solo necesita saber qué variedad es y si el
          // dato de auditoría vecino sigue vigente para decidir si
          // esa parcela de al lado es "de otra finca con contrato"
          // o "sin contrato" (que es lo que realmente le interesa).
          push(t("catalogue.variety", "Variedad"), props.variety_name);
          if (props.is_audited) {
            const years = props.years_since_last_audit;
            const dateLbl = props.last_audited_at ?? "";
            const yearsLbl =
              years == null
                ? ""
                : years <= 0
                  ? ` (${t("map.nearby_audited_recent", "este año")})`
                  : ` (${t("map.nearby_audited_years_ago", "hace")} ${years} ${
                      years === 1
                        ? t("common.year", "año")
                        : t("common.years", "años")
                    })`;
            rows.push(
              `<strong>${t("map.nearby_last_audit", "Última auditoría")}:</strong> ` +
                `<span style="background:#f6ffed;color:#237804;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;">` +
                `${escape(dateLbl)}${yearsLbl}</span>`,
            );
          } else {
            rows.push(
              `<strong>${t("map.nearby_last_audit", "Última auditoría")}:</strong> ` +
                `<span style="background:#fff1f0;color:#a8071a;padding:1px 8px;border-radius:10px;font-size:11px;font-weight:600;">` +
                `${escape(t("map.nearby_never_audited", "Sin auditar"))}</span>`,
            );
          }
          if (props.distance_m != null) {
            const km = (props.distance_m / 1000).toFixed(
              props.distance_m < 1000 ? 2 : 1,
            );
            push(t("map.nearby_distance", "Distancia"), `${km} km`);
          }
          break;
        }
        case "sample": {
          push(t("sample.code", "Código"), props.name);
          push(t("sample.plot_name", "Parcela"), props.plot_name);
          push(t("sample.waypoint", "Waypoint"), props.waypoint);
          // Chip coloreado con la MISMA paleta que la bandera del
          // marker (`SAMPLE_STATUS_FILL`) para que el popup y el
          // icono coincidan visualmente. Fondos claros (na/pending)
          // usan texto oscuro para contraste.
          if (props.result_status) {
            const CHIP_FILL: Record<
              NonNullable<MapFeature["properties"]["result_status"]>,
              string
            > = {
              tango: "#52c41a",
              no_tango: "#ff4d4f",
              na: "#ffffff",
              pending: "#faad14",
              mixed: "#722ed1",
              nc: "#40a9ff",
              other_variety: "#eb2f96",
            };
            const CHIP_LABEL: Record<
              NonNullable<MapFeature["properties"]["result_status"]>,
              string
            > = {
              tango: "Tango",
              no_tango: "No Tango",
              na: "No Amplificada",
              pending: "Pendiente",
              mixed: "Divergente",
              nc: "No Concluyente",
              other_variety: "Otra variedad",
            };
            const colour = CHIP_FILL[props.result_status] ?? "#bfbfbf";
            const label = props.state_name || CHIP_LABEL[props.result_status];
            const textColour =
              props.result_status === "na" || props.result_status === "pending"
                ? "#333"
                : "#fff";
            rows.push(
              `<strong>${escape(t("sample.result", "Resultado"))}:</strong> ` +
                `<span style="display:inline-block;padding:1px 8px;border-radius:10px;background:${colour};color:${textColour};font-size:11px;font-weight:600;">${escape(label)}</span>`,
            );
          }
          // Notas de la muestra (Sample v2 → `Sample.notes`). Solo
          // aparece si tiene contenido — misma info que en el KML.
          if (props.notes && props.notes.trim()) {
            rows.push(
              `<strong>${escape(t("sample.notes", "Notas"))}:</strong> ${escape(props.notes.trim())}`,
            );
          }
          break;
        }
        default:
          break;
      }
      return rows;
    };

    const titleFor = (lt: MapFeature["properties"]["layer_type"]): string => {
      switch (lt) {
        case "audit_plot":
          return `🌱 ${t("map.audit_geometry", "Parcela auditada")}`;
        case "farm_plot":
          return `📐 ${t("map.farm_geometry", "Parcela de finca")}`;
        case "licensed_plot":
          return `🟦 ${t("map.licensed_plot", "Parcela licenciada")}`;
        case "adjacent_crop":
          return `🟣 ${t("map.adjacent_crop", "Cultivo colindante")}`;
        case "control_point":
          return `🔴 ${t("map.control_point", "Punto de control")}`;
        case "sample":
          return `🚩 ${t("map.sample", "Muestra")}`;
        case "route":
          return `📍 ${t("map.route", "Ruta del auditor")}`;
        case "nearby_protected_plot":
          return `🟪 ${t("map.nearby_protected_plot", "Parcela vecina protegida")}`;
        default:
          return "";
      }
    };
    // Now that escape + titleFor are defined, re-point the
    // forward-declared references the chooser closure captured at
    // the top of this effect.
    escapeFn = escape;
    titleForFn = titleFor;

    const actionsFor = (props: MapFeature["properties"]): string => {
      // Capas de referencia (farm_plot, licensed_plot) son sólo
      // lectura — no permitimos editar/borrar. El resto lleva sus
      // botones editar+borrar habituales.
      if (
        props.layer_type === "farm_plot"
        || props.layer_type === "licensed_plot"
        || !props.id
      ) return "";
      const id = escape(String(props.id));
      const lt = props.layer_type;
      return `
        <div style="margin-top:10px;display:flex;gap:6px;justify-content:flex-end;">
          <button
            type="button"
            data-action="edit"
            data-layer-type="${lt}"
            data-id="${id}"
            style="background:#1677ff;color:#fff;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:12px;">
            ${escape(t("common.edit", "Editar"))}
          </button>
          <button
            type="button"
            data-action="delete"
            data-layer-type="${lt}"
            data-id="${id}"
            data-name="${escape(props.name ?? "")}"
            style="background:#ff4d4f;color:#fff;border:none;padding:4px 10px;border-radius:4px;cursor:pointer;font-size:12px;">
            ${escape(t("common.delete", "Eliminar"))}
          </button>
        </div>`;
    };

    const buildPopup = (props: MapFeature["properties"]) => {
      return `
        <div style="font-family: Arial, sans-serif; min-width: 220px;">
          <strong style="font-size:13px;color:#000;">${titleFor(props.layer_type)}</strong>
          <hr style="margin:5px 0;border:none;border-top:1px solid #ddd;"/>
          ${rowsFor(props).join("<br/>")}
          ${actionsFor(props)}
        </div>`;
    };

    const styleFor = (
      lt: MapFeature["properties"]["layer_type"],
      props?: MapFeature["properties"],
    ): L.PathOptions | undefined => {
      switch (lt) {
        case "audit_plot":
          return auditStyle;
        case "farm_plot":
          return farmStyle;
        case "licensed_plot":
          return licensedPlotStyle;
        case "adjacent_crop":
          return (props as any)?.closed_here
            ? adjacentCropClosedHereStyle
            : adjacentCropStyle;
        case "control_point":
          return (props as any)?.closed_here
            ? controlPointClosedHereStyle
            : controlPointStyle;
        case "route":
          return routeStyle;
        case "nearby_protected_plot":
          return nearbyProtectedStyle;
        default:
          return undefined;
      }
    };

    // Standard Leaflet pin in different colors via the well-known
    // pointhi/leaflet-color-markers PNGs (served from jsdelivr's gh
    // mirror). Same shape as the default Leaflet marker — just a
    // different hue per layer.
    const COLOR_MARKER_BASE =
      "https://cdn.jsdelivr.net/gh/pointhi/leaflet-color-markers@master/img";
    const SHADOW_URL = "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png";
    const pinIcon = (color: "red" | "violet" | "blue"): L.Icon =>
      L.icon({
        iconUrl: `${COLOR_MARKER_BASE}/marker-icon-${color}.png`,
        iconRetinaUrl: `${COLOR_MARKER_BASE}/marker-icon-2x-${color}.png`,
        shadowUrl: SHADOW_URL,
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
      });

    // Icon for control points closed in *this* audit — same red
    // pin shape but with a darker tone + a white ✕ stamp so the
    // map reads unambiguously "this one was closed today".
    const closedControlPointIcon = (): L.DivIcon =>
      L.divIcon({
        className: "audit-map-cp-closed",
        html: `
          <div style="
            position:relative;
            width:24px;
            height:32px;
            filter:drop-shadow(0 1px 1.5px rgba(0,0,0,0.4));
          ">
            <div style="
              position:absolute;
              left:2px;
              top:0;
              width:20px;
              height:24px;
              background:#a8071a;
              border:2px solid #ffffff;
              border-radius:50% 50% 50% 0;
              transform:rotate(-45deg);
            "></div>
            <div style="
              position:absolute;
              left:0;
              top:8px;
              width:24px;
              text-align:center;
              color:#fff;
              font-weight:700;
              font-size:14px;
              line-height:1;
              pointer-events:none;
            ">✕</div>
          </div>`,
        iconSize: [24, 32],
        iconAnchor: [12, 28],
        popupAnchor: [0, -28],
      });

    // Flag-shaped divIcon para muestras. El color de la bandera
    // sigue la misma paleta que el KML descargable:
    //   tango         → verde   (#52c41a)
    //   no_tango      → rojo    (#ff4d4f)
    //   na            → blanco  (#ffffff)         — No Amplificada
    //   pending       → amarillo (#faad14)
    //   mixed         → morado  (#722ed1)         — divergente
    //   nc            → azul    (#40a9ff)         — No Concluyente
    //   other_variety → rosa    (#eb2f96)         — otra variedad
    const SAMPLE_STATUS_FILL: Record<
      NonNullable<MapFeature["properties"]["result_status"]>,
      string
    > = {
      tango: "#52c41a",
      no_tango: "#ff4d4f",
      na: "#ffffff",
      pending: "#faad14",
      mixed: "#722ed1",
      nc: "#40a9ff",
      other_variety: "#eb2f96",
    };
    const flagIcon = (
      status: NonNullable<MapFeature["properties"]["result_status"]>,
    ): L.DivIcon => {
      const fill = SAMPLE_STATUS_FILL[status] ?? "#faad14";
      // Trazo más suave en fondos claros (blanco/pending) para que la
      // bandera no se pierda; oscuro sobre los colores saturados.
      const stroke =
        status === "na" || status === "pending" ? "#595959" : "#1a1a1a";
      const html = `
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 32"
             width="24" height="32" style="display:block;
             filter:drop-shadow(0 1px 1.5px rgba(0,0,0,0.35));">
          <line x1="4" y1="2" x2="4" y2="30"
                stroke="${stroke}" stroke-width="2" stroke-linecap="round"/>
          <path d="M4 4 L20 4 L17 11 L20 18 L4 18 Z"
                fill="${fill}" stroke="${stroke}" stroke-width="1.2"
                stroke-linejoin="round"/>
        </svg>`;
      return L.divIcon({
        html,
        className: "audit-map-sample-flag",
        iconSize: [24, 32],
        // Anchor at the bottom of the pole so the flag plants on the
        // exact GPS point.
        iconAnchor: [4, 30],
        popupAnchor: [8, -28],
      });
    };

    // Accumulator for bounds that drive the initial centring. We
    // deliberately exclude `route` features — the GPS track is a
    // reference layer, not the subject of the map; including it
    // would zoom out to follow long tracks and miss the parcelas.
    let focusBounds: L.LatLngBounds | null = null;

    for (const feature of features) {
      const layerType = feature.properties.layer_type;
      if (
        layerType !== "audit_plot" &&
        layerType !== "farm_plot" &&
        layerType !== "licensed_plot" &&
        layerType !== "adjacent_crop" &&
        layerType !== "control_point" &&
        layerType !== "sample" &&
        layerType !== "route" &&
        layerType !== "nearby_protected_plot"
      ) {
        continue;
      }
      // Honor the legend toggle: hidden layers are not added to the
      // feature group at all, which removes their markers and
      // popups cleanly.
      if (!layerVisible[layerType]) continue;
      try {
        const style = styleFor(layerType, feature.properties);
        const pinColor: "red" | "violet" | "blue" =
          layerType === "control_point"
            ? "red"
            : layerType === "adjacent_crop"
              ? "violet"
              : "blue";
        const isClosedHere =
          (layerType === "control_point" || layerType === "adjacent_crop") &&
          Boolean((feature.properties as any)?.closed_here);
        const geo = L.geoJSON(feature as any, {
          style: style,
          // Point geometries render as either a coloured pin (control
          // points / adjacent crops without polygon) or a coloured
          // flag (samples — colour by lab result). Entities closed
          // in this audit (CPs OR adjacent crops) get a dedicated
          // divIcon with a ✕ overlay so it's obvious on the map.
          pointToLayer: (_, latlng) => {
            if (layerType === "sample") {
              const status =
                feature.properties.result_status ?? "pending";
              return L.marker(latlng, { icon: flagIcon(status) });
            }
            if (isClosedHere) {
              return L.marker(latlng, {
                icon: closedControlPointIcon(),
              });
            }
            return L.marker(latlng, { icon: pinIcon(pinColor) });
          },
          onEachFeature: (_, lyr) => {
            lyr.bindPopup(buildPopup(feature.properties));
            if (isClosedHere) {
              lyr.bindTooltip(
                t(
                  "map.closed_here_tooltip",
                  "Dado de baja en esta auditoría",
                ) as string,
                { permanent: false, direction: "top" },
              );
            }
            // Stash the base style on the leaf so the overlap-
            // chooser button handler can restore + highlight this
            // specific layer without recomputing styleFor() later.
            (lyr as any)._auditMapBaseStyle = style ?? {};
          },
        });
        // Click-to-highlight + overlap chooser. Attached on the
        // wrapper AFTER construction — referencing `geo` from inside
        // onEachFeature would hit the const TDZ and the surrounding
        // try/catch would silently swallow the feature. Markers
        // don't have setStyle, so we skip points; they already pop
        // visually.
        const geoAsAny = geo as any;
        if (typeof geoAsAny.setStyle === "function") {
          geoAsAny.on("click", (e: any) => {
            // Overlap check: if the click point also falls inside
            // OTHER polygons, swap the auto-opened popup for a
            // chooser so the lower geometries stay reachable.
            const latLng = e?.latlng as L.LatLng | undefined;
            if (latLng) {
              const matches = findOverlappingPolygons(latLng);
              if (matches.length > 1) {
                showOverlapChooser(latLng, matches);
                return;
              }
            }
            // Single polygon under cursor → standard highlight flow.
            clearHighlight();
            const base: L.PathOptions = style ?? {};
            const highlightStyle: L.PathOptions = {
              ...base,
              color: "#fadb14",
              weight: 5,
              // Override the dashed baseline so the active outline
              // reads as a solid bright loop.
              dashArray: undefined,
              fillOpacity: Math.min(0.55, (base.fillOpacity ?? 0.3) + 0.2),
            };
            try {
              geoAsAny.setStyle(highlightStyle);
              geoAsAny.bringToFront?.();
              highlightedRef.current = { layer: geoAsAny, baseStyle: base };
            } catch {
              /* ignore — layer may be transient */
            }
          });
        }
        layer.addLayer(geo);
        // `nearby_protected_plot` también se excluye de focusBounds:
        // están a 500 m–20 km del centroide de la finca, así que
        // incluirlas alejaría la cámara y perderíamos de vista la
        // auditoría (que es el sujeto real del mapa).
        if (layerType !== "route" && layerType !== "nearby_protected_plot") {
          try {
            const b = (geo as any).getBounds?.() as
              | L.LatLngBounds
              | undefined;
            if (b && b.isValid()) {
              focusBounds = focusBounds ? focusBounds.extend(b) : b;
            }
          } catch {
            /* skip features with no bounds */
          }
        }
      } catch {
        // Skip malformed geometries silently.
      }
    }

    mapInstance.addLayer(layer);
    featuresLayerRef.current = layer;

    // Wire edit/delete buttons inside popups. Leaflet renders popups
    // as static HTML, so we attach a click listener on `popupopen` to
    // the freshly mounted container and dispatch by `data-action`.
    const onPopupOpen = (e: L.PopupEvent) => {
      const el = e.popup.getElement();
      if (!el) return;
      const handler = (ev: Event) => {
        const target = (ev.target as HTMLElement | null)?.closest(
          "button[data-action]",
        ) as HTMLButtonElement | null;
        if (!target) return;
        const action = target.dataset.action;
        const layerType = target.dataset.layerType;
        const id = target.dataset.id;
        const name = target.dataset.name ?? "";
        if (!action || !layerType || !id) return;

        if (action === "edit") {
          mapInstance.closePopup();
          if (layerType === "audit_plot") {
            navigate(`/audits/${auditId}/plots/${id}/edit`);
          } else if (layerType === "control_point") {
            navigate(`/control-points/${id}`);
          } else if (layerType === "adjacent_crop") {
            navigate(`/adjacent-crops/edit/${id}`);
          } else if (layerType === "sample") {
            // Sample edits abren un modal en show.tsx (no hay ruta
            // propia). Delegamos vía callback: si el padre no lo
            // provee, hacemos scroll a la tabla de muestras como
            // fallback razonable para no dejar el botón muerto.
            if (onEditSample) {
              onEditSample(String(id));
            } else {
              document
                .getElementById("audit-samples-card")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
            }
          }
        } else if (action === "delete") {
          modal.confirm({
            title: t("common.delete_confirm_title", "¿Eliminar?"),
            icon: <ExclamationCircleFilled />,
            content: name
              ? t(
                  "common.delete_confirm_named",
                  `Se eliminará "${name}". Esta acción no se puede deshacer.`,
                  { name },
                )
              : t(
                  "common.delete_confirm",
                  "Esta acción no se puede deshacer.",
                ),
            okText: t("common.delete", "Eliminar"),
            okButtonProps: { danger: true },
            cancelText: t("common.cancel", "Cancelar"),
            onOk: async () => {
              const ok = await deleteFeature(layerType, id);
              if (ok) {
                mapInstance.closePopup();
                fetchMapData();
              }
            },
          });
        }
      };
      el.addEventListener("click", handler);
      // Detach when the popup closes — avoids leaks across re-opens.
      mapInstance.once("popupclose", () => {
        el.removeEventListener("click", handler);
      });
    };
    mapInstance.on("popupopen", onPopupOpen);
    // Restore the original outline when the popup closes (either by
    // the user clicking the × or by clicking another feature, which
    // makes Leaflet auto-close the prior popup).
    mapInstance.on("popupclose", clearHighlight);

    // Centre on the audit only when the backend data itself
    // changed (or this is the first render). Toggling a legend
    // chip — including the "Rutas" one — rebuilds the layer but
    // must NOT move the view. The bounds we fit to deliberately
    // exclude routes, so a long GPS track doesn't drag the camera
    // out of the parcelas on the initial centring either.
    if (fittedForMapDataRef.current !== mapData) {
      if (focusBounds && focusBounds.isValid()) {
        mapInstance.fitBounds(focusBounds, { padding: [30, 30] });
      }
      fittedForMapDataRef.current = mapData;
    }

    return () => {
      mapInstance.off("popupopen", onPopupOpen);
      mapInstance.off("popupclose", clearHighlight);
    };
  }, [mapInstance, mapData, mergedMapData, t, navigate, auditId, deleteFeature, fetchMapData, layerVisible, onEditSample]);

  return (
    <Card
      title={
        <Space>
          <EnvironmentOutlined />
          <span>{t("audit.map_card_title", "Mapa de la auditoría")}</span>
          {auditReference && <Tag color="default">{auditReference}</Tag>}
        </Space>
      }
      bordered={false}
      style={
        fillHeight
          ? {
              marginBottom: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }
          : { marginBottom: 24 }
      }
      // The static colour tags that used to live in `extra` have been
      // promoted to an interactive legend rendered below the map (see
      // the JSX at the bottom of the Card body). Keeping `extra`
      // empty avoids duplicating the same swatches twice.
      loading={loading && !mapData}
      styles={{
        body: fillHeight
          ? {
              padding: 12,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }
          : { padding: 12 },
      }}
    >
      <Space
        style={{
          marginBottom: 8,
          width: "100%",
          justifyContent: "space-between",
          flexWrap: "wrap",
        }}
        wrap
      >
        {/* Read-only audits skip every create-mode option except
            plain "Ver mapa". The whole toolbar disappears so the
            user isn't tempted to draw anything that the backend
            would reject. */}
        {readOnly ? (
          <Segmented<CreateMode>
            value="none"
            disabled
            options={[
              { value: "none", label: t("audit.create_none", "Ver mapa") },
            ]}
          />
        ) : (
          <Segmented<CreateMode>
            value={createMode}
            onChange={(v) => setCreateMode(v as CreateMode)}
            options={[
              { value: "none", label: t("audit.create_none", "Ver mapa") },
              { value: "plot", label: t("audit.create_plot", "+ Parcela") },
              { value: "plot-gpx", label: t("audit.create_plot_gpx", "+ Parcela (GPX/KML)") },
              { value: "control-point", label: t("audit.create_pc", "+ Punto de control") },
              { value: "adjacent-crop", label: t("audit.create_adjacent_crop", "+ Variedad colindante") },
            ]}
          />
        )}
        {createMode !== "none" && createMode !== "plot-gpx" && (
          <span style={{ color: "#8c8c8c", fontSize: 12 }}>
            {createMode === "control-point"
              ? t(
                  "audit.create_pc_hint",
                  "Pulsa el icono ◉ del mapa y haz clic donde quieras colocar el punto",
                )
              : t(
                  "audit.create_polygon_hint",
                  "Pulsa el icono ▱ del mapa y dibuja el polígono",
                )}
          </span>
        )}
        {createMode === "plot-gpx" && (
          <span style={{ color: "#8c8c8c", fontSize: 12 }}>
            {t(
              "audit.create_plot_gpx_hint",
              "Selecciona un .gpx (track o waypoints) o un .kml (Polygon, LineString o 3+ Placemark Point)",
            )}
          </span>
        )}
        {/* Plot-map snapshot — captures the satellite view with
            ONLY the audit plots (falling back to the farm-level
            plot boundary when no AuditPlots exist yet), each one
            tagged with a translucent name pill. */}
        <Tooltip
          title={t(
            "audit.export_plot_map_tooltip",
            "Descarga una imagen del mapa con las parcelas y sus nombres.",
          )}
        >
          <Button
            icon={<CameraOutlined />}
            loading={exportingPlotMap}
            onClick={exportPlotMapPng}
            disabled={!mapInstance || !mapData}
          >
            {t("audit.export_plot_map", "Descargar mapa de parcelas")}
          </Button>
        </Tooltip>
        {/* KML descargable con parcelas + ruta del auditor + muestras
            (coloreadas por resultado) + puntos de control + variedades
            colindantes. Abrir en Google Earth / QGIS / Maps.me. */}
        <Tooltip
          title={t(
            "audit.download_kml_tooltip",
            "Descarga un .kml con parcelas, ruta del día, muestras (color según resultado), puntos de control y colindantes.",
          )}
        >
          <Button
            icon={<DownloadOutlined />}
            loading={downloadingKml}
            onClick={downloadAuditKml}
          >
            {t("audit.download_kml", "Descargar KML")}
          </Button>
        </Tooltip>
        {/* "Vecinos protegidos": switch + selector de radio. Se
             muestra siempre a la derecha del toolbar para que el
             auditor pueda activarla sin depender de que primero
             existan features en la leyenda (chicken-and-egg). */}
        <Space size="small" wrap>
          <Tooltip
            title={t(
              "audit.nearby_protected_tooltip",
              "Muestra parcelas cercanas de variedad protegida (anonimizado). Ayuda a distinguir vecinos con contrato de parcelas sin contrato.",
            )}
          >
            <Space size={6}>
              <Switch
                size="small"
                checked={layerVisible.nearby_protected_plot}
                loading={nearbyLoading}
                onChange={(checked) =>
                  setLayerVisible((prev) => ({
                    ...prev,
                    nearby_protected_plot: checked,
                  }))
                }
              />
              <span style={{ fontSize: 12, color: "#595959" }}>
                {t("map.nearby_protected_plot", "Vecinos protegidos")}
              </span>
            </Space>
          </Tooltip>
          <Segmented<1 | 2 | 5 | 10>
            size="small"
            value={nearbyRadiusKm}
            disabled={!layerVisible.nearby_protected_plot}
            onChange={(v) => setNearbyRadiusKm(v as 1 | 2 | 5 | 10)}
            options={[
              { value: 1, label: "1 km" },
              { value: 2, label: "2 km" },
              { value: 5, label: "5 km" },
              { value: 10, label: "10 km" },
            ]}
          />
        </Space>
      </Space>
      <input
        ref={gpxInputRef}
        type="file"
        accept=".gpx,.kml,application/gpx+xml,application/vnd.google-earth.kml+xml,application/xml,text/xml"
        style={{ display: "none" }}
        onChange={handleGpxFile}
      />
      {fillHeight ? (
        // Wrapper absorbs the leftover vertical space inside the Card
        // body so the map can render at height=100%. `position:
        // relative` + absolute child guarantees the map renders
        // even when the flex height resolution is fragile.
        <div
          style={{
            flex: 1,
            minHeight: 320,
            position: "relative",
            width: "100%",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
            }}
          >
            <CustomMap
              height="100%"
              showLayerControl
              showAddressSearch={false}
              showSearchBar={false}
              onMapReady={(map) => setMapInstance(map)}
            />
          </div>
        </div>
      ) : (
        <CustomMap
          height={mapHeight}
          showLayerControl
          showAddressSearch={false}
          showSearchBar={false}
          onMapReady={(map) => setMapInstance(map)}
        />
      )}

      {/* Interactive legend — sits below the map (chart-style),
          one chip per layer with a colour swatch + count. Clicking
          a chip toggles that layer's visibility. Hidden chips fade
          out and the geometries leave the map. */}
      <MapLegendBar
        features={mergedMapData?.features ?? mapData?.features ?? []}
        visible={layerVisible}
        onToggle={toggleLayer}
      />
    </Card>
  );
});
AuditMapCard.displayName = "AuditMapCard";

export default AuditMapCard;
